import React, { useState } from 'react';
import { TIFFIN_MENU_ITEMS, getAllCategories } from '../data/menuItems';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, X } from '@phosphor-icons/react';

const ComboSelector = ({ onComboSelect }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [comboName, setComboName] = useState('');
  const [comboPrice, setComboPrice] = useState('');
  const [error, setError] = useState('');
  const categories = getAllCategories();

  const toggleItem = (item) => {
    const itemKey = `${item.name}-${item.category}`;
    const isSelected = selectedItems.some(i => `${i.name}-${i.category}` === itemKey);
    
    if (isSelected) {
      setSelectedItems(selectedItems.filter(i => `${i.name}-${i.category}` !== itemKey));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
    setError('');
  };

  const isItemSelected = (item) => {
    const itemKey = `${item.name}-${item.category}`;
    return selectedItems.some(i => `${i.name}-${i.category}` === itemKey);
  };

  const handleCreateCombo = () => {
    if (!comboName.trim()) {
      setError('Please enter a combo name');
      return;
    }
    if (!comboPrice || parseFloat(comboPrice) <= 0) {
      setError('Please enter a valid combo price');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Please select at least one item');
      return;
    }

    const combo = {
      name: comboName.trim(),
      items: selectedItems.map(item => item.name).join(', '),
      price: comboPrice
    };

    onComboSelect(combo);
    setComboName('');
    setComboPrice('');
    setSelectedItems([]);
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Combo Details */}
      <Card className="bg-brand-accent/10 border-brand-primary">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Combo Package Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="combo_name" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
              Combo Name *
            </Label>
            <Input
              id="combo_name"
              value={comboName}
              onChange={(e) => { setComboName(e.target.value); setError(''); }}
              placeholder="e.g., Full Breakfast Combo, Lunch Special"
              className="rounded-none border-[#E8E1D5]"
              data-testid="combo-name-input"
            />
          </div>

          <div>
            <Label htmlFor="combo_price" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
              Combo Price (₹) *
            </Label>
            <Input
              id="combo_price"
              type="number"
              value={comboPrice}
              onChange={(e) => { setComboPrice(e.target.value); setError(''); }}
              placeholder="Total price for the combo"
              className="rounded-none border-[#E8E1D5]"
              data-testid="combo-price-input"
            />
          </div>

          <div className="p-3 bg-white border border-[#E8E1D5]">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
              Selected Items ({selectedItems.length})
            </div>
            {selectedItems.length === 0 ? (
              <p className="text-sm text-text-secondary">No items selected yet. Select items below.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedItems.map((item, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-sm rounded">
                    {item.name} <span className="text-green-600 font-medium">₹{item.defaultPrice}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-300 text-red-700 text-sm" data-testid="combo-error-msg">
              {error}
            </div>
          )}

          <Button
            type="button"
            onClick={handleCreateCombo}
            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="create-combo-btn"
          >
            Add Combo Package
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <h3 className="text-xl font-heading font-medium">Select Items for Combo</h3>
        <div className="text-sm text-text-secondary">
          Selected: {selectedItems.length} items
        </div>
      </div>

      {/* Item Selection */}
      {categories.map((category) => {
        const categoryItems = TIFFIN_MENU_ITEMS.filter(item => item.category === category);
        return (
          <Card key={category} className="bg-white border-[#E8E1D5]">
            <CardHeader>
              <CardTitle className="text-lg font-heading">{category}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {categoryItems.map((item, idx) => {
                  const selected = isItemSelected(item);
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 border rounded-md transition-all cursor-pointer ${
                        selected 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-[#E8E1D5] bg-white hover:border-brand-primary'
                      }`}
                      onClick={() => toggleItem(item)}
                      data-testid={`combo-item-${item.name.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleItem(item);
                        }}
                        className={`rounded-full w-10 h-10 p-0 flex-shrink-0 ${
                          selected
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-red-500 hover:bg-red-600'
                        } text-white`}
                        data-testid={`toggle-combo-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        {selected ? <Check size={20} weight="bold" /> : <X size={20} weight="bold" />}
                      </Button>
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm font-semibold text-brand-primary">₹{item.defaultPrice}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ComboSelector;
